import React, { useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormFieldLayout,
  FormReadonlyField,
  FormSelectField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ClientDto } from 'api/Clients/Types/Client'
import { WalletType } from 'api/Transaction/Types/Transaction'
import { WalletDto } from 'api/Wallet/Types/Wallet'
import { WalletToWalletTransfer } from 'api/Wallet/Types/WalletToWalletTransfer'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Link } from 'components/Link'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import toNumber from 'lodash/toNumber'
import { Circle, CircleDot, TriangleAlert } from 'lucide-react'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'

function dataToSubmit(values: TransferFormValues): FormSubmitValues<WalletToWalletTransfer> {
  return {
    fromWalletId: values.fromWalletId,
    toWalletId: values.toWalletId,
    amount: 0,
    comment: values.comment,
    fromAmount: values.transferSource === WalletType.DEBIT ? toNumber(values.debitAmount) : 0,
    toAmount: values.transferSource === WalletType.CREDIT ? toNumber(values.creditAmount) : 0,
  }
}

const TransferFormUI: React.FC<
  FormikProps<TransferFormValues> & OuterProps & TransferFormContainerProps
> = (props) => {
  const {
    client,
    errors,
    values,
    isSubmitting,
    setFieldValue,
    exchangeRate,
    hasDebitRestrictions,
    hasCreditRestrictions,
    debitTooltipTitle,
    creditTooltipTitle,
    onSubmitForm,
  } = props

  useEffect(() => {
    props.resetForm()
  }, [client?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const debitWalletWarning =
    hasDebitRestrictions && debitTooltipTitle ? (
      <Tooltip>
        <TooltipTrigger>
          <TriangleAlert size={14} />
        </TooltipTrigger>
        <TooltipContent side='top'>{`Restrictions: ${debitTooltipTitle}`}</TooltipContent>
      </Tooltip>
    ) : undefined

  const creditWalletWarning =
    hasCreditRestrictions && creditTooltipTitle ? (
      <Tooltip>
        <TooltipTrigger>
          <TriangleAlert size={14} />
        </TooltipTrigger>
        <TooltipContent side='top'>{`Restrictions: ${creditTooltipTitle}`}</TooltipContent>
      </Tooltip>
    ) : undefined

  const debitWalletRightAddon =
    values.fromWalletId || debitWalletWarning ? (
      <>
        {values.fromWalletId && (
          <Link
            to={generatePath(links.recordsWallets.detailsUrl as string, {
              id: values.fromWalletId,
              slug: 'details',
            })}
            showIcon
          />
        )}
        {debitWalletWarning}
      </>
    ) : undefined

  const creditWalletRightAddon =
    values.toWalletId || creditWalletWarning ? (
      <>
        {values.toWalletId && (
          <Link
            to={generatePath(links.recordsWallets.detailsUrl as string, {
              id: values.toWalletId,
              slug: 'details',
            })}
            showIcon
          />
        )}
        {creditWalletWarning}
      </>
    ) : undefined

  return (
    <Form onSubmit={onSubmitForm} data-test='transaction-form'>
      <DataGrid columns={1}>
        <DataColumn>
          <DataCard title='Transfer' columns={2}>
            <FormReadonlyField
              name='client'
              label='Client'
              value={values.client ?? ''}
              dataTest='client-field'
              rightAddon={
                <>
                  <Button type='button' size='sm' variant='outline' onClick={props.setClient}>
                    Select
                  </Button>
                  {client?.id && (
                    <Link
                      to={generatePath(links.recordsClients.detailsUrl as string, {
                        id: client.id,
                        slug: 'details',
                      })}
                      showIcon
                    />
                  )}
                </>
              }
            />
            <FormSelectField
              name='fromWalletId'
              label='Debit Wallet'
              value={values.fromWalletId ?? undefined}
              options={props.wallets.map((wallet) => ({
                id: wallet.id,
                name: `${wallet.name}-balance:${wallet.balance}`,
              }))}
              error={errors.fromWalletId}
              onChange={(value) => {
                const nextWalletId = value ? String(value) : ''
                setFieldValue('fromWalletId', nextWalletId || null)
                setFieldValue('toWalletId', null)
                props.onSelectDebitWallet(nextWalletId)
              }}
              rightAddon={debitWalletRightAddon}
            />
            <FormSelectField
              name='toWalletId'
              label='Credit Wallet'
              value={values.toWalletId ?? undefined}
              options={props.creditWallets.map((wallet) => ({
                id: wallet.id,
                name: `${wallet.name}-balance:${wallet.balance}`,
              }))}
              error={errors.toWalletId}
              onChange={(value) => {
                const nextWalletId = value ? String(value) : ''
                setFieldValue('toWalletId', nextWalletId || null)
                props.onSelectCreditWallet(nextWalletId)
              }}
              rightAddon={creditWalletRightAddon}
            />
            <FormReadonlyField
              label='Exchange Rate'
              value={props.exchangeRate > 0 ? props.exchangeRate : ''}
            />
            <FormFieldLayout name='debitAmount' label='Debit Amount' error={errors.debitAmount}>
              <Button
                type='button'
                variant={values.transferSource === WalletType.DEBIT ? 'default' : 'outline'}
                size='icon-xs'
                onClick={() => {
                  setFieldValue('transferSource', WalletType.DEBIT)
                  props.setRates(WalletType.DEBIT)
                }}
                aria-pressed={values.transferSource === WalletType.DEBIT}
                aria-label='Use debit amount as source'
              >
                {values.transferSource === WalletType.DEBIT ? <CircleDot /> : <Circle />}
              </Button>
              <Input
                id='debitAmount'
                name='debitAmount'
                min='0'
                type='number'
                step={0.01}
                value={values.debitAmount}
                disabled={values.transferSource !== WalletType.DEBIT}
                onChange={(event) => {
                  const desiredAmount = Number(event.target.value)
                  setFieldValue('debitAmount', event.target.value)
                  setFieldValue('creditAmount', (exchangeRate * desiredAmount).toFixed(2))
                }}
                data-test='debit-amount-number-field'
              />
              {props.selectedDebitWallet?.currency.id && (
                <span className='inline-flex items-center px-3 text-sm text-muted-foreground'>
                  {props.selectedDebitWallet.currency.id}
                </span>
              )}
            </FormFieldLayout>
            <FormFieldLayout name='creditAmount' label='Credit Amount' error={errors.creditAmount}>
              <Button
                type='button'
                variant={values.transferSource === WalletType.CREDIT ? 'default' : 'outline'}
                size='icon-xs'
                onClick={() => {
                  setFieldValue('transferSource', WalletType.CREDIT)
                  props.setRates(WalletType.CREDIT)
                }}
                aria-pressed={values.transferSource === WalletType.CREDIT}
                aria-label='Use credit amount as source'
              >
                {values.transferSource === WalletType.CREDIT ? <CircleDot /> : <Circle />}
              </Button>
              <Input
                id='creditAmount'
                name='creditAmount'
                min='0'
                type='number'
                step={0.01}
                value={values.creditAmount}
                disabled={values.transferSource !== WalletType.CREDIT}
                onChange={(event) => {
                  const desiredAmount = Number(event.target.value)
                  setFieldValue('creditAmount', event.target.value)
                  setFieldValue('debitAmount', (exchangeRate * desiredAmount).toFixed(2))
                }}
                data-test='credit-amount-number-field'
              />
              {props.selectedCreditWallet?.currency.id && (
                <span className='inline-flex items-center px-3 text-sm text-muted-foreground'>
                  {props.selectedCreditWallet.currency.id}
                </span>
              )}
            </FormFieldLayout>
            <FormTextareaField
              name='comment'
              label='Comment'
              rows={3}
              maxLength={130}
              value={values.comment ?? ''}
            />
            <FormFieldLayout label='Info'>
              <span className='inline-flex min-h-9 items-center px-3 text-sm text-muted-foreground'>
                * Amounts are calculated based on an indicative real-time rate
              </span>
            </FormFieldLayout>
          </DataCard>
        </DataColumn>
      </DataGrid>
      <div className='mt-4 flex justify-center'>
        <Button disabled={isSubmitting} data-test='submit' type='submit'>
          Submit
        </Button>
      </div>
    </Form>
  )
}

interface TransferFormContainerProps {
  onSubmitForm: (event: React.FormEvent<HTMLFormElement>) => void
}

const TransferFormContainer: React.FC<FormikProps<TransferFormValues> & OuterProps> = (props) => {
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)
  const [isRestrictionModalOpen, setRestrictionModalOpen] = useState<boolean>(false)

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const nextErrors = await props.validateForm(props.values)
    const isValid = Object.keys(nextErrors).length === 0

    if (!isValid) {
      return
    }

    if (props.hasDebitRestrictions || props.hasCreditRestrictions) {
      handleRestrictionOpenModal()
      return
    }

    handleConfirmationOpenModal()
  }

  const handleRestrictionSubmit = async () => {
    handleConfirmationOpenModal()
    handleRestrictionCloseModal()
  }

  const handleConfirmationSubmit = async () => {
    try {
      await props.submitForm()
      handleConfirmationCloseModal()
    } catch (e) {
      console.error(e)
      handleConfirmationCloseModal()
    }

    resetAll()
  }

  const handleConfirmationOpenModal = () => {
    setConfirmationModalOpen(true)
  }

  const handleConfirmationCloseModal = () => {
    setConfirmationModalOpen(false)
  }

  const handleRestrictionOpenModal = () => {
    setRestrictionModalOpen(true)
  }

  const handleRestrictionCloseModal = () => {
    setRestrictionModalOpen(false)
  }

  const resetAll = () => {
    props.resetForm()
    props.resetAll()
  }

  return (
    <>
      {isConfirmationModalOpen && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={handleConfirmationSubmit}
          onCancel={handleConfirmationCloseModal}
          visible={isConfirmationModalOpen}
          renderTitle={
            <div>
              <p>
                You are going to transfers funds from the wallet {props.selectedDebitWallet?.name}{' '}
                to the wallet {props.selectedCreditWallet?.name}. The parameters selected are:
              </p>
              <div>
                <div id='field'>
                  <strong>Client:</strong> {props?.client?.name}
                </div>
                <div id='field'>
                  <strong>Debit Wallet:</strong> {props.selectedDebitWallet?.name}
                </div>
                <div id='field'>
                  <strong>Credit Wallet:</strong> {props.selectedCreditWallet?.name}
                </div>
                <div id='field'>
                  <strong>Debit Amount:</strong> {props.values?.debitAmount}{' '}
                  {props?.selectedDebitWallet?.currency?.id}
                </div>
                <div id='field'>
                  <strong>Credit Amount:</strong> {props.values?.creditAmount}{' '}
                  {props?.selectedCreditWallet?.currency?.id}
                </div>
              </div>
            </div>
          }
        />
      )}
      {isRestrictionModalOpen && (
        <ConfirmationModal
          yesButtonText='Yes, proceed'
          onYes={handleRestrictionSubmit}
          onCancel={handleRestrictionCloseModal}
          visible={isRestrictionModalOpen}
          renderTitle={
            <div>There is a restriction in place which agent can acknowledge before proceeding</div>
          }
        />
      )}
      <TransferFormUI {...props} onSubmitForm={handleSubmitForm} />
    </>
  )
}

interface TransferFormValues {
  client: string | null
  fromWalletId: string | null
  toWalletId: string | null
  comment: string | null
  transferSource: WalletType | null
  debitAmount: number | string
  creditAmount: number | string
}

interface OuterProps {
  client: ClientDto | null
  wallets: WalletDto[]
  creditWallets: WalletDto[]
  exchangeRate: number
  selectedDebitWallet?: WalletDto
  selectedCreditWallet?: WalletDto

  onSubmit(values: FormSubmitValues<WalletToWalletTransfer>): Promise<void>
  setRates(type: WalletType): void
  setClient(): void
  onSelectDebitWallet(walletId: string): void
  onSelectCreditWallet(walletId: string): void
  resetAll(): void

  debitTooltipTitle?: string
  creditTooltipTitle?: string
  hasCreditRestrictions: boolean
  hasDebitRestrictions: boolean
}

export const TransferForm = withFormik<OuterProps, TransferFormValues>({
  mapPropsToValues: ({ client, selectedDebitWallet }) => {
    const currentClient: Partial<ClientDto> = client || {}
    return {
      client: currentClient.name || null,
      fromWalletId: selectedDebitWallet ? selectedDebitWallet.id : null,
      toWalletId: null,
      creditAmount: 0,
      debitAmount: 0,
      comment: '',
      transferSource: null,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<TransferFormValues> = {}

    if (!values.toWalletId) {
      errors.toWalletId = 'Required'
    }

    if (!toNumber(values.creditAmount)) {
      errors.creditAmount = 'Required'
    }

    if (!toNumber(values.debitAmount)) {
      errors.debitAmount = 'Required'
    }

    if (!values.fromWalletId) {
      errors.fromWalletId = 'Required'
    }

    return errors
  },
  enableReinitialize: true,
})(TransferFormContainer)
